import React, { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
// import { toast } from "react-toastify";
import { Card, Button, Row, Col, Container, OverlayTrigger, Tooltip, Form, Pagination } from "react-bootstrap";
import '../App.css';
import { CartContext } from "../CartContext"; // Import CartContext
import { fetchProducts, fetchCategoriesApi } from "../api/api.js"; // Ensure fetchProducts handles pagination if backend is paginated
import PriceAlertsBanner from "./pricealerts";

const POS = () => {
    const [products, setProducts] = useState([]);
    const [searchTerm, setSearchTerm] = useState(''); // Search term for products
    const [categories, setCategories] = useState([]); // Categories for filtering products
    const [selectedCategory, setSelectedCategory] = useState(''); // Selected category for filtering products
    // const [discountAmount, setDiscountAmount] = useState(0); // Discount amount for the cart
    const [priceMode, setPriceMode] = useState('retail'); // To keep track of selected price mode
    const [currentPage, setCurrentPage] = useState(1); // Current page for pagination
    const [totalPages, setTotalPages] = useState(1); // Total pages for pagination

    const { clearCart, cart, updateCart, addToCart, removeFromCart, discount, setDiscount } = useContext(CartContext); // Destructure CartContext

    const VAT_RATE = 0.16; // VAT rate for calculating VAT

    const navigate = useNavigate();

    const handleBack = () => {
        navigate(-1);
    };

    // Fetch products when the component mounts or dependencies change
   useEffect(() => {
    const loadProducts = async () => {
        try {
            const productsData = await fetchProducts(currentPage, searchTerm, selectedCategory);
            // console.log('Products loaded:', productsData); // Should show count and results

            if (productsData && Array.isArray(productsData.results)) {
                setProducts(productsData.results);
                setTotalPages(Math.ceil(productsData.count / 20)); // Adjust page size as needed
            } else {
                console.error('Invalid products data structure:', productsData);
                setProducts([]);
                setTotalPages(1);
            }
        } catch (error) {
            console.error('Failed to load products:', error);
            setProducts([]);
            setTotalPages(1);
        }
    };
    loadProducts().then(r => console.log('Products loaded:', r)).catch(console.error);
}, [currentPage, searchTerm, selectedCategory]);

    // Fetch categories when the component mounts
    useEffect(() => {
        const loadCategories = async () => {
            try {
                const categoriesData = await fetchCategoriesApi();
                // console.log('Categories data:', categoriesData)
                setCategories(categoriesData);
            } catch (error) {
                console.error('Failed to load categories:', error);
            }
        };

        loadCategories().then(r => console.log('Categories loaded:', r)).catch(console.error);
    }, []);

    // updates the searchTerm and reset the currentPage to 1 whenever the user types in the search bar
    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    // Function to update the quantity of a product
    const updateQuantity = (productId, action) => {
        setProducts(products.map(product => {
            if (product.id === productId) {
                let updatedQuantity = product.selectedQuantity || 1;
                if (action === 'increment' && updatedQuantity < product.stock) {
                    updatedQuantity += 1;
                } else if (action === 'decrement' && updatedQuantity > 1) {
                    updatedQuantity -= 1;
                }
                return { ...product, selectedQuantity: updatedQuantity };
            }
            return product;
        }));
    };

    const handleQuantityChange = (productId, newQuantity) => {
        // added
        const parsedQuantity = parseInt(newQuantity, 10);

        // if not a number or < 1, show alert
        if (isNaN(parsedQuantity) || parsedQuantity < 1) {
            alert('Quantity cannot be less than 1');
            return;
        }
        // end of added

        setProducts(currentProducts =>
            currentProducts.map(product => {
                if (product.id === productId) {
                    // Parse the input value to ensure it's a number and clamp it to the stock range
                    // let validQuantity = Math.min(Math.max(1, Number(newQuantity)), product.stock);
                    // return { ...product, selectedQuantity: validQuantity };

                    // added
                    if (parsedQuantity > product.stock) {
                        alert('Quantity exceeds available stock');
                        // clamp to product.stock or return as it is
                        return { ...product, selectedQuantity: product.stock };
                    }
                    return { ...product, selectedQuantity: parsedQuantity };
                    // end of added
                }
                return product;
            })
        );
    };

    // Function to handle unit changes
    const handleUnitChange = (productId, unitId) => {
        setProducts(currentProducts =>
            currentProducts.map(product => {
                if (product.id !== productId) return product;

                // Determine the base price based on price mode
                const basePrice = priceMode === 'retail'
                    ? parseFloat(product.retail_price) || 0.00
                    : parseFloat(product.wholesale_price) || 0.00;

                if (unitId === 'base_unit') {
                    // Reset to base unit
                    return {
                        ...product,
                        selectedUnitId: null,
                        conversion_factor: 1.0,
                        unit_price: basePrice,
                        price: basePrice,
                        unit_name: product.base_unit?.name || 'Base Unit',
                    };
                }

                // Parse unitId to integer
                const parsedUnitId = parseInt(unitId);
                if (!isNaN(parsedUnitId)) {
                    const selectedUnit = product.units.find(
                        (unit) => unit.unit.id === parsedUnitId
                    );

                    if (selectedUnit) {
                        // Determine the unit price to display based on price mode
                        const unitPrice = priceMode === 'retail'
                            ? parseFloat(selectedUnit.unit_price) || basePrice
                            : parseFloat(selectedUnit.unit_wholesale_price) || basePrice;

                        return {
                            ...product,
                            selectedUnitId: parsedUnitId,
                            conversion_factor: parseFloat(selectedUnit.conversion_factor) || 1.0,
                            unit_price: unitPrice,
                            price: unitPrice,
                            unit_name: selectedUnit.unit.name || product.base_unit?.name || 'Base Unit',
                        };
                    }
                }

                // If unitId is invalid or not found, return product as is
                return product;
            })
        );
    };

    // Function to handle adding products to the cart via CartContext
    const handleAddToCart = async (product) => {
        try {
            // Default quantity to 1 if not set
            const quantityToAdd = Math.floor(product.selectedQuantity || 1);

            if (quantityToAdd > product.stock) {
                alert('Quantity exceeds available stock');
                return;
            }

            // Determine the correct price based on the selected unit
            const basePrice = priceMode === 'retail'
                ? parseFloat(product.retail_price) || 0.00
                : parseFloat(product.wholesale_price) || 0.00;

            let finalPrice = basePrice; // Default to base price
            let chosenUnitName = product.base_unit?.name || 'Base Unit';

            if (product.selectedUnitId) {
                const selectedUnit = product.units.find(
                    (unit) => unit.unit.id === parseInt(product.selectedUnitId)
                );
                if (selectedUnit) {
                    finalPrice = priceMode === 'retail'
                        ? parseFloat(selectedUnit.unit_price) || basePrice
                        : parseFloat(selectedUnit.unit_wholesale_price) || basePrice;
                    chosenUnitName = selectedUnit.unit.name;
                }
            }
            // storing a flag isAlternativeUnit to indicate if the selected unit is different from the base unit
            // const isAltUnit = !!product.selectedUnitId;

            const productToAdd = {
                id: product.id,
                name: product.name,
                selectedQuantity: quantityToAdd,
                selectedUnitId: product.selectedUnitId, // Can be null for base unit
                price: finalPrice, // VAT-inclusive price to backend
                unit_name: chosenUnitName,
                // isAlternativeUnit: isAltUnit,
            };
            await addToCart(productToAdd);
        } catch (error) {
            console.error('Error adding product to cart:', error);
            // toast.error('Failed to add product to cart');
        }
    };


    // Function to handle checkout navigation
    const handleCheckout = () => {
        navigate('/POS/checkout', { state: { cart, discount } });
    };

    // Calculate pre-tax subtotal
    const getLineSubtotal = (item) => Number(item.line_subtotal ?? (item.price * item.quantity)) || 0;
    const getLineDiscount = (item) => Number(item.quantity_discount ?? 0) || 0;
    const getDiscountedLineSubtotal = (item) => Math.max(0, getLineSubtotal(item) - getLineDiscount(item));
    const getQuantityDiscountTotal = () => cart.reduce((total, item) => total + getLineDiscount(item), 0);

    const calculatePreTaxSubtotal = () => {
        return cart.reduce((total, item) => {
            const itemTotal = getDiscountedLineSubtotal(item);
            if (item.is_vat_applicable) {
                const preTaxPrice = itemTotal / (1 + VAT_RATE);
                return total + preTaxPrice;
            } else {
                return total + itemTotal;
            }
        }, 0);
    };

    // Calculate VAT amount
    const calculateVatAmount = () => {
        return cart.reduce((total, item) => {
            if (item.is_vat_applicable) {
                const itemTotal = getDiscountedLineSubtotal(item);
                const preTaxPrice = itemTotal / (1 + VAT_RATE);
                const vatAmount = itemTotal - preTaxPrice;
                return total + vatAmount;
            }
            return total;
        }, 0);
    };

    // Calculate the total cost of items in the cart
    const calculateTotal = () => {
        const preTaxSubtotal = calculatePreTaxSubtotal();
        const vatAmount = calculateVatAmount();
        const total = preTaxSubtotal + vatAmount - discount;
        return Math.ceil(total); // Adjust as needed (e.g., rounding)
    };

    const maxPagesToShow = 5; // Maximum number of pagination buttons to show

    let startPage = Math.max(currentPage - Math.floor(maxPagesToShow / 2), 1);
    let endPage = startPage + maxPagesToShow - 1;

    if (endPage > totalPages) {
        endPage = totalPages;
        startPage = Math.max(endPage - maxPagesToShow + 1, 1);
    }
    const pagesToShow = [];
    for (let i = startPage; i <= endPage; i++) {
        pagesToShow.push(i);
    }

    return (
        <div>
            <div className="back-button-container">
                <Button onClick={handleBack} variant="secondary">Back</Button>
            </div>
            <Container fluid>
                <PriceAlertsBanner compact />
                <h1>Point of Sale</h1>
                <Row className="mb-3">
                    <Col md={6} className="search-bar">
                        <input
                            type="text"
                            placeholder="Search products..."
                            value={searchTerm}
                            // onChange={(e) => setSearchTerm(e.target.value)}
                            onChange={handleSearchChange}
                            className="form-control"
                        />
                    </Col>
                    <Col md={3}>
                        <select
                            value={selectedCategory}
                            onChange={(e) => {
                                // console.log('Category selected:', e.target.value);
                                setSelectedCategory(e.target.value)}}
                            className="form-control"
                        >
                            <option value="">All categories</option>
                            {categories.map((category) => (
                                <option key={category.id} value={category.id}>{category.category_name}</option>
                            ))}
                        </select>
                    </Col>
                    <Col md={3} className="text-right">
                        {/* Price mode selector */}
                        <Form.Group controlId="priceModeSelect">
                            <Form.Control
                                as="select"
                                value={priceMode}
                                onChange={(e) => setPriceMode(e.target.value)}
                            >
                                <option value="retail">Retail</option>
                                <option value="wholesale">Wholesale</option>
                            </Form.Control>
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col md={7} className="product-display-section">
                        <Row>
                            {products.map((product) => {
                                let displayPrice;

                                if (product.selectedUnitId) {
                                    const unitDetails = product.units.find(
                                        (unit) => unit.unit.id === parseInt(product.selectedUnitId)
                                    );

                                    if (unitDetails) {
                                        displayPrice = priceMode === 'retail'
                                            ? parseFloat(unitDetails.unit_price) || 0.00
                                            : parseFloat(unitDetails.unit_wholesale_price) || 0.00;
                                    } else {
                                        displayPrice = priceMode === 'retail'
                                            ? parseFloat(product.retail_price) || 0.00
                                            : parseFloat(product.wholesale_price) || 0.00;
                                    }
                                } else {
                                    displayPrice = priceMode === 'retail'
                                        ? parseFloat(product.retail_price) || 0.00
                                        : parseFloat(product.wholesale_price) || 0.00;
                                }

                                return (
                                    <Col sm={6} md={4} lg={3} key={product.id} className="mb-4">
                                        <Card className="product-card">
                                            <Card.Body>
                                                <OverlayTrigger
                                                    key={product.id}
                                                    placement="top"
                                                    overlay={
                                                        <Tooltip id={`tooltip-${product.id}`}>
                                                            {product.name}
                                                        </Tooltip>
                                                    }
                                                >
                                                    <Card.Title>{product.name}</Card.Title>
                                                </OverlayTrigger>
                                                <Card.Text>
                                                    Ksh.{displayPrice.toFixed(2)}
                                                </Card.Text>
                                                {/* Units & alternative units selection */}
                                                {product.units && product.units.length > 0 ? (
                                                    <Form.Control
                                                        as="select"
                                                        value={product.selectedUnitId ?? 'base_unit'}
                                                        onChange={(e) => handleUnitChange(product.id, e.target.value)}
                                                    >
                                                        {/* Add base unit option */}
                                                        <option value='base_unit'>Base Unit
                                                            ({product.base_unit?.name || 'Base Unit'})
                                                        </option>
                                                        {product.units.map((unit) => (
                                                            <option key={unit.unit.id}
                                                                    value={unit.unit.id}>{unit.unit.name}</option>
                                                        ))}
                                                    </Form.Control>
                                                ) : (
                                                    <span>Base Unit</span>
                                                )}
                                                <div className="quantity-controls">
                                                    <Button variant="outline-secondary" size="sm" onClick={() =>
                                                        updateQuantity(product.id, 'decrement')}
                                                            disabled={product.selectedQuantity <= 1}>-</Button>
                                                    <input
                                                        type='number'
                                                        className='quantity-input'
                                                        value={product.selectedQuantity || 1}
                                                        onChange={(e) =>
                                                            handleQuantityChange(product.id, e.target.value)}
                                                        min='1'
                                                        max={product.stock} />
                                                    <Button variant="outline-secondary" size="sm"
                                                            onClick={() => updateQuantity(product.id, 'increment')}
                                                            disabled={product.selectedQuantity >= product.stock}>+</Button>
                                                </div>
                                                {/* Use CartContext's addToCart */}
                                                <Button variant="primary" onClick={() => handleAddToCart(product)}>Add to
                                                    cart</Button>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                );
                            })}
                        </Row>
                        {/* Frontend Pagination Controls */}
                        <Pagination className="justify-content-center mt-4">
                            <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                            <Pagination.Prev onClick={() => setCurrentPage(prev =>
                                Math.max(prev - 1, 1))} disabled={currentPage === 1} />
                            {pagesToShow.map(page => (
                                <Pagination.Item
                                    key={page}
                                    active={page === currentPage}
                                    onClick={() => setCurrentPage(page)}
                                >
                                    {page}
                                </Pagination.Item>
                            ))}
                            <Pagination.Next onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                             disabled={currentPage === totalPages} />
                            <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
                        </Pagination>
                    </Col>

                    {/* Cart Section */}
                    <Col md={5} className="cart-section">
                        <div className="cart-header">
                            <h2>Cart</h2>
                            <Button onClick={clearCart} variant="danger">Clear Cart</Button>
                        </div>
                        <table className="table">
                            <thead>
                            <tr>
                                <th>Product</th>
                                <th>Unit Type</th>
                                <th>Quantity</th>
                                <th>Unit Price</th>
                                <th>Subtotal</th>
                                <th>Action</th>
                            </tr>
                            </thead>
                            <tbody>
                            {cart.map((item, index) => (
                                <tr key={item.id}>
                                    <td>
                                        <div>{item.name}</div>
                                        {getLineDiscount(item) > 0 && (
                                            <div className="text-success small">
                                                {item.quantity_discount_reason || `Quantity Discount: -Ksh. ${getLineDiscount(item).toFixed(2)}`}
                                            </div>
                                        )}
                                    </td>
                                    <td>{item.unit_name || 'Base Unit'}</td>
                                    <td>
                                        <input
                                            type="number"
                                            value={item.quantity}
                                            onChange={(e) => {
                                                const newQuantity = parseInt(e.target.value);
                                                if (!isNaN(newQuantity)) {
                                                    updateCart(index, { ...item, quantity: newQuantity });
                                                }
                                            }}
                                            min="1"
                                            max={item.stock}
                                            style={{ width: '60px' }} />
                                    </td>
                                    <td>KSh.{parseFloat(item.price).toFixed(2)}</td>
                                    <td>
                                        <div>KSh.{getDiscountedLineSubtotal(item).toFixed(2)}</div>
                                        {getLineDiscount(item) > 0 && (
                                            <div className="text-muted small">
                                                Before Discount: KSh.{getLineSubtotal(item).toFixed(2)}
                                            </div>
                                        )}
                                    </td>
                                    <td>
                                        <button onClick={() => removeFromCart(item.id)} className="btn btn-danger">X</button>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                        {/* Discount Section */}
                        <div className="row justify-content-center mb-3">
                            <div className="col-auto">
                                <Form className="d-inline">
                                    <Form.Label htmlFor="discountInput" className="mr-2">Discount:</Form.Label>
                                    <Form.Control
                                        type="number"
                                        id="discountInput"
                                        placeholder="0"
                                        value={discount}
                                        onChange={(e) => setDiscount(Math.max(0,
                                            parseFloat(e.target.value) || 0))}
                                        style={{ width: '100px' }}
                                        className="form-control mb-3" />
                                </Form>
                            </div>
                        </div>
                        {/* Subtotal and VAT Breakdown */}
                        <div className="text-center mb-2">
                            <h4>Subtotal (pre VAT): Ksh.{calculatePreTaxSubtotal().toFixed(2)}</h4>
                        </div>
                        <div className="text-center mb-2">
                            <h4>VAT: Ksh.{calculateVatAmount().toFixed(2)}</h4>
                        </div>
                        {discount > 0 && (
                            <div className="text-center mb-2 text-danger">
                                <h4>Discount: -Ksh.{discount.toFixed(2)}</h4>
                            </div>
                        )}
                        {getQuantityDiscountTotal() > 0 && (
                            <div className="text-center mb-2 text-success">
                                <h4>Quantity Discount: -Ksh.{getQuantityDiscountTotal().toFixed(2)}</h4>
                            </div>
                        )}
                        <div className="text-center">
                            <h3>Total: Ksh.{calculateTotal()}</h3>
                        </div>
                        <div className="text-center">
                            <Button onClick={handleCheckout} variant="success">Checkout</Button>
                        </div>
                    </Col>
                </Row>
            </Container>
    </div>
    );
};

export default POS;
